/** Raid aggro adapter — table-driven amounts on engine AggroTable. */

import { raidCatalog } from "../../data/raid-catalog";
import {
  addAggroModifier,
  addThreat,
  createAggroTable,
  decayThreat,
  threatRanks,
  topThreatTarget,
  type AggroTable,
} from "../../sim";
import type { RaidClassId } from "../../data/raid-catalog";
import type { RaidBoss, RaidUnit } from "./raid-types";
import aggroJson from "../../data/tables/raid/aggro.json";

/** Sentinel target id for the boss inside a unit's aggro table. */
export const RAID_AGGRO_BOSS_ID = -1;

type AggroCfg = typeof aggroJson;

function cfg(): AggroCfg {
  return (raidCatalog as { aggro?: AggroCfg }).aggro ?? aggroJson;
}

function engageCfg() {
  return cfg().engage;
}

export function createRaidAggro(): AggroTable {
  return createAggroTable();
}

export function raidAggroSticky() {
  const c = cfg();
  return {
    stickyBonus: c.stickyBonus,
    minSwitchGain: c.minSwitchGain,
  };
}

/** Softcap stored boss threat so mid-fight dump cannot lock engage. */
export function engageBossScore(raw: number): number {
  return Math.min(raw, engageCfg().bossSoftCap);
}

/** In-range add urgency — must beat softCap + sticky + minSwitchGain. */
export function peelInRangeBonus(): number {
  return engageCfg().peelInRange;
}

/** Invariant: in-range peel beats softcapped boss even at max personal sticky. */
export function peelBeatsBossSticky(): boolean {
  const e = engageCfg();
  const mapping = raidCatalog.profiles.mapping as {
    stickyGain?: { max?: number };
  };
  const stickyMax = mapping.stickyGain?.max ?? cfg().stickyBonus;
  // Derived minSwitch at personal01=1 (stickyGain max endpoint family).
  const minSwitchMax = 16;
  return e.peelInRange > e.bossSoftCap + stickyMax + minSwitchMax;
}

export function noteDamageThreat(
  table: AggroTable,
  targetId: number,
  damage: number,
  classId?: RaidClassId,
): void {
  const c = cfg();
  let amount = damage * c.weights.damage;
  if (classId && c.roleBias[classId]) {
    amount += c.roleBias[classId];
  }
  addThreat(table, targetId, amount);
}

export function noteToughnessThreat(
  table: AggroTable,
  targetId: number,
  chip: number,
): void {
  addThreat(table, targetId, chip * cfg().weights.toughnessChip);
}

export function noteShieldBashTaunt(
  bossAggro: AggroTable,
  tankId: number,
  time: number,
): void {
  const c = cfg();
  addThreat(bossAggro, tankId, c.weights.shieldBash);
  addAggroModifier(bossAggro, tankId, c.taunt.mul, time + c.taunt.durationSec);
}

export function noteMarkedShotThreat(
  bossAggro: AggroTable,
  dpsId: number,
): void {
  addThreat(bossAggro, dpsId, cfg().weights.markedShot);
}

export function noteFocusTaunt(
  bossAggro: AggroTable,
  tankId: number,
  time: number,
  until: number,
): void {
  const c = cfg();
  addAggroModifier(bossAggro, tankId, c.focus.tankMul, until);
  addThreat(bossAggro, tankId, c.focus.preferBossBonus);
}

/** Spawn bias so the boss prefers tanks before anyone has dealt damage. */
export function seedTankAggroBias(boss: RaidBoss, tank: RaidUnit): void {
  const bias = cfg().roleBias.tank ?? 0;
  if (bias > 0) addThreat(boss.aggro, tank.id, bias);
}

/**
 * Pulse peel urgency onto nearby allies toward an add (spawn / ally hit).
 * Restores "new threat" response without distance-only engage.
 */
export function notePeelPulse(
  allies: readonly RaidUnit[],
  add: RaidUnit,
  time: number,
  opts?: { tanksOnly?: boolean },
): void {
  if (add.hp <= 0 || add.team !== 1) return;
  const e = engageCfg();
  const r2 = e.pulseRadius * e.pulseRadius;
  for (const a of allies) {
    if (a.hp <= 0 || a.team !== 0) continue;
    if (opts?.tanksOnly && a.classId !== "tank") continue;
    const dx = a.x - add.x;
    const dy = a.y - add.y;
    if (dx * dx + dy * dy > r2) continue;
    addThreat(a.aggro, add.id, e.peelPulse);
    addAggroModifier(a.aggro, add.id, e.peelPulseMul, time + e.peelPulseSec);
  }
}

export function decayRaidAggro(table: AggroTable, dt: number): void {
  decayThreat(table, dt, cfg().decayPerSec);
}

export function pickBossAggroTarget(
  boss: RaidBoss,
  allies: readonly RaidUnit[],
  time: number,
  currentId: number,
): RaidUnit | null {
  const living = allies.filter((a) => a.hp > 0 && a.team === 0);
  if (!living.length) return null;
  return topThreatTarget(boss.aggro, living, {
    idOf: (u) => u.id,
    currentId,
    sticky: raidAggroSticky(),
    time,
    bonusOf: (u) => {
      // Slight proximity so zero-threat ties break toward nearest.
      const d = Math.hypot(u.x - boss.x, u.y - boss.y);
      return Math.max(0, 40 - d * 0.05);
    },
  });
}

export function bossThreatHudRanks(
  boss: RaidBoss,
  allies: readonly RaidUnit[],
  time: number,
): { unitId: number; threat: number; rank: number }[] {
  const ids = allies.filter((a) => a.hp > 0 && a.team === 0).map((a) => a.id);
  return threatRanks(boss.aggro, ids, time, cfg().hudTopN).map((r) => ({
    unitId: r.targetId,
    threat: r.threat,
    rank: r.rank,
  }));
}

/**
 * Living primary aggro target for HUD arrow (sticky pickBossAggroTarget).
 * Null when boss has no aggro scores, no living ally, boss is dead, or boss is
 * CC'd / break-stunned (`stunnedUntil` — same gate as tickBoss AI freeze).
 */
export function resolveBossAggroHudTarget(
  boss: RaidBoss,
  allies: readonly RaidUnit[],
  time: number,
): RaidUnit | null {
  if (boss.barsTotal - boss.barIndex <= 0) return null;
  if (time < boss.stunnedUntil) return null;
  if (boss.aggro.scores.size === 0) return null;
  return pickBossAggroTarget(boss, allies, time, boss.aggroTargetId);
}

export function pickLeapAggroTarget(
  boss: RaidBoss,
  allies: readonly RaidUnit[],
  time: number,
): RaidUnit | null {
  const backline = allies.filter(
    (a) => a.hp > 0 && a.team === 0 && (a.classId === "healer" || a.classId === "dps"),
  );
  if (!backline.length) return null;
  const bias = cfg().leapBacklineBias;
  return topThreatTarget(boss.aggro, backline, {
    idOf: (u) => u.id,
    currentId: boss.aggroTargetId,
    sticky: raidAggroSticky(),
    time,
    bonusOf: (u) =>
      (u.classId === "healer" ? bias : bias * 0.5) + u.y * 0.02,
  });
}
